import { useState, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { convertFileToBase64, updateProgressSteps, parseLine, resolveStreamPhaseAction } from './useQuestionHandlerHelpers';

// What a dropped request shows.  Once its stream has started the server keeps
// going and saves the result, so the answer can still arrive; before that,
// nothing ran.
const droppedRequestMessage = (error, streamStarted) =>
  streamStarted
    ? 'Connection interrupted — your request is still processing and the ' +
      'answer will appear here when it completes.'
    : (error.message || 'Failed to fetch response.');

// The response of a long answer or evaluation whose request dropped.  It stays
// under the answer it was for.  `awaiting` is the entry field the run writes,
// so its arrival from the server (loadSessionHistory) ends the wait.
const droppedRunResponse = (error, streamStarted, entryIndex, awaiting) => ({
  summary: '',
  error: droppedRequestMessage(error, streamStarted),
  entryIndex,
  ...(streamStarted ? { awaiting } : {}),
});

// Surface circuit-breaker degraded-mode events as a toast.  The fixed
// id means repeated events (ask, expansive, and evaluation streams)
// update one toast instead of stacking duplicates.
const notifyDegradedMode = (json) => {
  if (json.status === 'degraded_mode' && json.message) {
    toast(json.message, {
      id: 'degraded-mode',
      icon: '⚠️',
      duration: 8000,
    });
  }
};

const DEFAULT_SESSION = {
  chatHistory: [],
  response: null,
  loading: false,
  progressSteps: [],
  pendingQuestion: "",
  pendingFiles: [],
  streamingPhase: 'preliminary',
  generatingExpansiveIndex: null,
  generatingEvaluationIndex: null,
  elapsed: null,
};

// Pure helper: apply an updater to one conversation within the sessions map.
const applyToSession = (prev, conversationId, updater) => ({
  ...prev,
  [conversationId]: updater(prev[conversationId] || DEFAULT_SESSION),
});

export const useChatSessions = (API_URL, modelSettings) => {
  const [sessions, setSessions] = useState({});

  // Ref mirrors state so async callbacks always read the latest values without
  // needing to be recreated (avoids stale-closure issues in streaming loops).
  const sessionsRef = useRef({});
  sessionsRef.current = sessions;

  // One AbortController per conversation; keyed by conversationId.
  const abortControllersRef = useRef({});

  const getSession = useCallback(
    (conversationId) => sessionsRef.current[conversationId] || DEFAULT_SESSION,
    [],
  );

  // Returns a Set of conversationIds that are currently loading.
  // Computed from reactive `sessions` state so callers re-render correctly.
  const getLoadingIds = useCallback(
    () => new Set(Object.keys(sessions).filter((id) => sessions[id]?.loading)),
    [sessions],
  );

  // Populate (or replace) the history for a conversation without touching its
  // loading / streaming state — used when loading from the server.
  //
  // forceServer=false (default): merges server entries with in-memory data so
  // that annotations set during streaming (preliminary_answer, evaluation_report)
  // are not lost when the server omits them (e.g. extraction gap after expansive
  // save, Supabase replication lag). In-memory is authoritative because it was
  // written by the just-completed streaming session.
  //
  // forceServer=true: skips the merge and uses server data directly. Use this
  // when the server has the definitive completed data and in-memory is stale
  // (cross-session completion detected via the justFinished polling path, or
  // when re-entering a conversation that was running cross-session). If the
  // in-memory data were merged here it would overwrite the fresh expansive
  // answer with the old concise answer, making hasPreliminary=false and leaving
  // the Expansive button green instead of switching to the blue view-switcher.
  const loadSessionHistory = useCallback((conversationId, history, forceServer = false) => {
    setSessions((prev) =>
      applyToSession(prev, conversationId, (s) => {
        const merged = history.map((serverEntry) => {
          if (forceServer) return serverEntry;
          const memEntry = s.chatHistory.find(
            (m) => m.index === serverEntry.index,
          );
          if (!memEntry) return serverEntry;
          return {
            ...serverEntry,
            assistant: memEntry.assistant || serverEntry.assistant,
            preliminary_answer:
              memEntry.preliminary_answer || serverEntry.preliminary_answer,
            evaluation_report:
              memEntry.evaluation_report || serverEntry.evaluation_report,
            agents_left_out:
              memEntry.agents_left_out || serverEntry.agents_left_out,
            evaluation_agents_left_out:
              memEntry.evaluation_agents_left_out || serverEntry.evaluation_agents_left_out,
          };
        });
        // Background-completion recovery: after a dropped stream the
        // server finishes on its own and this load delivers the
        // answer. Once the pending question appears answered in the
        // server history, clear the optimistic bubble and any stale
        // "connection interrupted" error so the conversation renders
        // clean. A pre-dispatch failure keeps its error (the pending
        // question was already cleared in that path, so it never
        // matches here).
        const pendingAnswered =
          !s.loading &&
          s.pendingQuestion &&
          merged.some((e) => e.user === s.pendingQuestion && e.assistant);
        const clearStaleError =
          pendingAnswered && s.response?.error && !s.response?.summary;
        // The same for a long answer or evaluation whose stream dropped.  Only
        // the server's copy counts: the stream left partial text in memory.
        const droppedRunFinished =
          s.response?.awaiting &&
          history.find((e) => e.index === s.response.entryIndex)?.[
            s.response.awaiting
          ];
        return {
          ...s,
          chatHistory: merged,
          ...(pendingAnswered
            ? { pendingQuestion: '', pendingFiles: [], progressSteps: [] }
            : {}),
          ...(clearStaleError || droppedRunFinished ? { response: null } : {}),
        };
      }),
    );
  }, []);

  const cancelRequest = useCallback(
    (conversationId, acceptAnswer = false, onAfterAnswer = null) => {
      const controller = abortControllersRef.current[conversationId];
      if (!controller) return;

      controller.abort();
      delete abortControllersRef.current[conversationId];

      setSessions((prev) => {
        const s = prev[conversationId] || DEFAULT_SESSION;

        if (acceptAnswer && s.response?.summary) {
          const newEntry = {
            user: s.pendingQuestion,
            assistant: s.response.summary,
            index: s.chatHistory.length,
          };
          // Schedule the callback after this synchronous state update.
          if (typeof onAfterAnswer === 'function') {
            setTimeout(() => onAfterAnswer(conversationId), 0);
          }
          return applyToSession(prev, conversationId, () => ({
            ...s,
            loading: false,
            generatingExpansiveIndex: null,
            generatingEvaluationIndex: null,
            chatHistory: [...s.chatHistory, newEntry],
            response: null,
            pendingQuestion: '',
            pendingFiles: [],
            progressSteps: [],
          }));
        }

        return applyToSession(prev, conversationId, () => ({
          ...s,
          loading: false,
          generatingExpansiveIndex: null,
          generatingEvaluationIndex: null,
          pendingQuestion: '',
          pendingFiles: [],
          response: s.response
            ? { ...s.response, error: 'Request cancelled by user.' }
            : { summary: '', error: 'Request cancelled by user.' },
        }));
      });
    },
    [],
  );

  // ─── /ask ────────────────────────────────────────────────────────────────────

  const askQuestion = useCallback(
    (conversationId, question, files, onAfterAnswer) => {
      if (!question.trim() && files.length === 0) return false;
      if (sessionsRef.current[conversationId]?.loading) return false;

      (async () => {
      const controller = new AbortController();
      abortControllersRef.current[conversationId] = controller;
      const startTime = performance.now();

      // Snapshot chatHistory before async gap; use local var in streaming loop.
      const initialHistory = [...(sessionsRef.current[conversationId]?.chatHistory || [])];

      setSessions((prev) =>
        applyToSession(prev, conversationId, (s) => ({
          ...s,
          loading: true,
          response: null,
          elapsed: null,
          progressSteps: [],
          streamingPhase: 'preliminary',
          pendingQuestion: question,
          pendingFiles: [...files],
        })),
      );

      let accumulatedSummary = '';
      let preliminaryAnswer = '';
      let fullAnswer = '';
      // True once the server accepted the request — a later stream
      // drop (Safari: "Load failed") does NOT stop the backend, which
      // finishes and persists; the status poll then delivers the
      // answer, so the failure is recoverable.
      let streamStarted = false;
      let currentPhase = 'preliminary';
      let newChatHistory = [...initialHistory];
      let isResponseComplete = false;
      // Index of the committed entry that owns the turn, plus the
      // accumulator for expansive text that streams AFTER that commit
      // (single-pass auto-escalation). Both are captured in commitEntry.
      let committedIndex = null;
      let accumulatedExpansive = '';

      // rAF-coalesce the per-token `response`/`chatHistory` writes: mutate the
      // accumulators synchronously each token but flush ONE setSessions per
      // animation frame.  Local to this async body — the :175 loading guard
      // blocks same-conversation re-entry, so no cross-conversation ref is
      // needed.  projectNow reads the live accumulators, so coalescing is
      // keep-last (correct for cumulative text).
      let rafId = null;
      let frameScheduled = false;
      const projectNow = () => {
        if (!isResponseComplete) {
          // Concise phase: drive the live streaming card.  The
          // !isResponseComplete guard means a stale frame that slips through
          // after commit takes the expansive branch instead — never
          // resurrecting `response` (the duplicate-card bug).
          setSessions((prev) =>
            applyToSession(prev, conversationId, (s) => ({
              ...s,
              response: { summary: accumulatedSummary, preliminaryAnswer, fullAnswer, error: null },
            })),
          );
        } else if (currentPhase === 'full') {
          // Single-pass expansive: update the committed entry in place.
          setSessions((prev) =>
            applyToSession(prev, conversationId, (s) => ({
              ...s,
              chatHistory: [...newChatHistory],
            })),
          );
        }
      };
      const runFrame = () => {
        frameScheduled = false;
        rafId = null;
        projectNow();
      };
      // Guard on `frameScheduled` (a boolean), NOT `rafId`: under the
      // synchronous rAF test stub, requestAnimationFrame runs runFrame BEFORE
      // assigning rafId, leaving rafId truthy — a rafId guard would wedge and
      // never schedule again.
      const scheduleProjection = () => {
        if (frameScheduled) return;
        frameScheduled = true;
        rafId = requestAnimationFrame(runFrame);
      };
      // Force-apply the pending frame synchronously.  Called before every
      // structural write (commit / phase transition / error / terminal) that
      // touches the same field the coalescer writes, so no stale delta frame
      // trails a commit.  React batches this setSessions with the immediately
      // following structural one, so last-write-wins ordering is preserved.
      const flushNow = () => {
        if (!frameScheduled) return;
        frameScheduled = false;
        if (rafId != null) cancelAnimationFrame(rafId);
        rafId = null;
        projectNow();
      };

      // Helper: commit a fully-resolved chat_entry to state.
      const commitEntry = (entry) => {
        flushNow();
        const idx = newChatHistory.findIndex((h) => h.index === entry.index);
        const normalized = {
          user: entry.user ?? '',
          assistant: entry.assistant ?? '',
          index: entry.index,
          ...(entry.files && { files: entry.files }),
          ...(entry.timestamp && { timestamp: entry.timestamp }),
          ...(entry.preliminary_answer && { preliminary_answer: entry.preliminary_answer }),
          ...(entry.evaluation_report && { evaluation_report: entry.evaluation_report }),
          ...(entry.agents_left_out && { agents_left_out: entry.agents_left_out }),
        };
        if (idx >= 0) {
          const ex = newChatHistory[idx];
          // Merge: preserve fields from the existing entry that the
          // incoming entry does not supply (e.g. preliminary_answer
          // is absent from evaluation-phase chat_entries, evaluation_report
          // is absent from Phase-1/2 chat_entries).
          const merged = {
            ...ex,
            ...normalized,
            preliminary_answer: normalized.preliminary_answer || ex.preliminary_answer,
            evaluation_report: normalized.evaluation_report || ex.evaluation_report,
          };
          if (
            ex.user !== merged.user ||
            ex.assistant !== merged.assistant ||
            ex.preliminary_answer !== merged.preliminary_answer ||
            ex.evaluation_report !== merged.evaluation_report ||
            ex.agents_left_out !== merged.agents_left_out
          ) {
            newChatHistory[idx] = merged;
          }
        } else {
          newChatHistory.push(normalized);
        }
        isResponseComplete = true;
        accumulatedSummary = '';
        committedIndex = entry.index;
        accumulatedExpansive = '';
        setSessions((prev) =>
          applyToSession(prev, conversationId, (s) => ({
            ...s,
            chatHistory: [...newChatHistory],
            response: null,
            pendingQuestion: '',
            pendingFiles: [],
            progressSteps: [],
          })),
        );
      };

      try {
        const processedFiles = await Promise.all(files.map((f) => convertFileToBase64(f)));

        const rawModelSettings = {
          max_tokens: modelSettings.maxTokens || null,
          temperature: modelSettings.temperature || null,
          top_p: modelSettings.topP || null,
          timeout: parseFloat(modelSettings.timeout) || null,
          presence_penalty: parseFloat(modelSettings.presencePenalty) || null,
          frequency_penalty: parseFloat(modelSettings.frequencyPenalty) || null,
        };
        Object.keys(rawModelSettings).forEach((k) => {
          if (rawModelSettings[k] === null || Number.isNaN(rawModelSettings[k])) {
            delete rawModelSettings[k];
          }
        });

        const requestBody = {
          question,
          provider: modelSettings.provider,
          model: modelSettings.model,
          agent_models: modelSettings.agentModelAssignments,
          conversation_id: conversationId,
          files: processedFiles.length > 0 ? processedFiles : null,
          ...(Object.keys(rawModelSettings).length > 0 && { model_settings: rawModelSettings }),
        };

        const res = await fetch(`${API_URL}/ask`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
          credentials: 'include',
          signal: controller.signal,
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({
            detail: `HTTP error! status: ${res.status}`,
          }));
          setSessions((prev) =>
            applyToSession(prev, conversationId, (s) => ({
              ...s,
              loading: false,
              elapsed: performance.now() - startTime,
              response: { summary: '', error: errorData.detail || `HTTP error! status: ${res.status}` },
              pendingQuestion: '',
              pendingFiles: [],
            })),
          );
          return;
        }

        streamStarted = true;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();

          for (const line of lines) {
            if (!line.trim()) continue;
            const json = parseLine(line);
            if (!json) continue;

            if (json.chat_entry) {
              commitEntry(json.chat_entry);
            }

            if (json.status) {
              notifyDegradedMode(json);
              // Concise → expansive phase boundary. The decision is a pure
              // helper (resolveStreamPhaseAction); see its docs for why
              // `expansive_starting` is the authoritative signal and why the
              // transition is idempotent (a later signal never resets
              // in-flight expansive content).
              const phaseAction = resolveStreamPhaseAction(
                json.status,
                json.message,
                currentPhase,
                accumulatedSummary.length > 0,
              );
              if (phaseAction !== 'none') {
                // Apply any pending concise frame with the CURRENT
                // accumulatedSummary before the transition resets it (and
                // cancel the frame so no stale delta trails this write).
                flushNow();
                if (phaseAction === 'transition') {
                  preliminaryAnswer = accumulatedSummary || preliminaryAnswer;
                  currentPhase = 'full';
                }
                accumulatedSummary = '';
                const phaseSnapshot = currentPhase;
                const prelimSnapshot = preliminaryAnswer;
                // Once Phase 1 (concise) has committed its chat_entry,
                // isResponseComplete is true and the turn is owned by that
                // committed entry; the expansive phase updates it in place.
                // Re-creating `response` here would resurrect the live
                // streaming card and render it as a DUPLICATE beside the
                // committed entry. Keep advancing streamingPhase (it drives
                // the committed entry's "generating" affordance) but only
                // drive the streaming card while it is still the active view.
                const committed = isResponseComplete;
                setSessions((prev) =>
                  applyToSession(prev, conversationId, (s) => ({
                    ...s,
                    streamingPhase: phaseSnapshot,
                    ...(committed
                      ? {}
                      : {
                          response: {
                            summary: '',
                            preliminaryAnswer: prelimSnapshot,
                            error: null,
                          },
                        }),
                  })),
                );
              }
              if (!['initializing', 'loading_history'].includes(json.status)) {
                setSessions((prev) =>
                  applyToSession(prev, conversationId, (s) => ({
                    ...s,
                    progressSteps: updateProgressSteps(s.progressSteps, json),
                  })),
                );
              }
            }

            if (json.summary_chunk && !isResponseComplete) {
              accumulatedSummary += json.summary_chunk;
              if (currentPhase === 'preliminary') preliminaryAnswer = accumulatedSummary;
              else fullAnswer = accumulatedSummary;
              scheduleProjection();
            }

            // Single-pass auto-escalation: the concise entry has already
            // committed (isResponseComplete) and the run advanced to the
            // expansive phase. The backend token-streams the expansive
            // answer as summary_chunks; route them into the committed
            // entry's `assistant` (preserving the concise as
            // preliminary_answer) so the existing Concise/Expansive
            // switcher renders and is readable as it streams — WITHOUT
            // re-creating `response`, which would resurrect the live
            // streaming card as a duplicate beside the committed entry.
            if (json.summary_chunk && isResponseComplete && currentPhase === 'full') {
              accumulatedExpansive += json.summary_chunk;
              // Snapshot the (reassignable) committedIndex into a const so
              // the findIndex closure is safe inside the streaming loop.
              const targetIndex = committedIndex;
              const cidx = newChatHistory.findIndex((h) => h.index === targetIndex);
              if (cidx >= 0) {
                const concise =
                  newChatHistory[cidx].preliminary_answer || newChatHistory[cidx].assistant;
                newChatHistory[cidx] = {
                  ...newChatHistory[cidx],
                  preliminary_answer: concise,
                  assistant: accumulatedExpansive,
                };
                scheduleProjection();
              }
            }

            if (json.error) {
              flushNow();
              const summarySnapshot = accumulatedSummary;
              const prelimSnapshot2 = preliminaryAnswer;
              const fullSnapshot = fullAnswer;
              setSessions((prev) =>
                applyToSession(prev, conversationId, (s) => ({
                  ...s,
                  response: { summary: summarySnapshot, preliminaryAnswer: prelimSnapshot2, fullAnswer: fullSnapshot, error: json.error },
                })),
              );
            }
          }
        }

        // Flush any remaining partial line.
        if (buffer.trim()) {
          try {
            const json = JSON.parse(buffer);
            if (json.chat_entry) commitEntry(json.chat_entry);
          } catch (err) {
            console.error('Error processing final buffer:', err);
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Fetch error:', error);
          // Mid-stream drops are recoverable: keep the question bubble
          // visible (the cross-session UI takes over) and show a
          // reassuring message instead of the raw browser error.
          // loadSessionHistory clears both once the answer arrives.
          const interrupted = streamStarted;
          flushNow();
          setSessions((prev) =>
            applyToSession(prev, conversationId, (s) => ({
              ...s,
              loading: false,
              response: {
                summary: '',
                error: droppedRequestMessage(error, interrupted),
              },
              ...(interrupted ? {} : { pendingQuestion: '', pendingFiles: [] }),
            })),
          );
          // The server continues running after a network drop and persists
          // results to DB. Reload so the user gets the result if it finished.
          if (typeof onAfterAnswer === 'function') {
            void onAfterAnswer(conversationId);
          }
        }
      } finally {
        // Only clean up if this controller is still the active one (guards against
        // a race where a newer request already replaced it).
        const active = abortControllersRef.current[conversationId] === controller;
        // Apply the pending stream tail before the terminal setSessions; on
        // abort (active === false) DROP it — cancelRequest already authored the
        // final state and flushing would overwrite the user's cancel.
        if (active) flushNow();
        if (rafId != null) cancelAnimationFrame(rafId);
        frameScheduled = false;
        rafId = null;
        if (active) {
          delete abortControllersRef.current[conversationId];
          setSessions((prev) =>
            applyToSession(prev, conversationId, (s) => ({
              ...s,
              loading: false,
              elapsed: performance.now() - startTime,
            })),
          );
          if (typeof onAfterAnswer === 'function') void onAfterAnswer(conversationId);
        }
      }
      })();

      return true;
    },
    [API_URL, modelSettings],
  );

  // ─── /generate_expansive ─────────────────────────────────────────────────────

  const generateExpansiveAnswer = useCallback(
    async (conversationId, messageIndex, onAfterAnswer, onExpansiveReady) => {
      if (sessionsRef.current[conversationId]?.loading) return;

      // Capture the original concise answer now, before streaming overwrites it.
      // Used in finally to guarantee preliminary_answer is always committed.
      const _initialEntry = (
        sessionsRef.current[conversationId] || DEFAULT_SESSION
      ).chatHistory[messageIndex];
      const originalConcise = _initialEntry?.assistant || '';

      const controller = new AbortController();
      abortControllersRef.current[conversationId] = controller;
      const startTime = performance.now();

      setSessions((prev) =>
        applyToSession(prev, conversationId, (s) => ({
          ...s,
          loading: true,
          generatingExpansiveIndex: messageIndex,
          progressSteps: [],
          response: null,
        })),
      );

      let accumulatedExpansive = '';
      let hasStartedStreaming = false;
      let streamStarted = false;

      try {
        const res = await fetch(`${API_URL}/generate_expansive`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            conversation_id: conversationId,
            message_index: messageIndex,
            provider: modelSettings.provider,
            model: modelSettings.model,
            agent_models: modelSettings.agentModelAssignments,
          }),
          credentials: 'include',
          signal: controller.signal,
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({
            detail: `HTTP error! status: ${res.status}`,
          }));
          const errMsg = res.status === 409
            ? (errData.detail || 'A pipeline is already running for this conversation.')
            : (errData.detail || `HTTP error! status: ${res.status}`);
          console.error('Error generating expansive answer:', res.status, errMsg);
          setSessions((prev) =>
            applyToSession(prev, conversationId, (s) => ({
              ...s,
              loading: false,
              progressSteps: [],
              generatingExpansiveIndex: null,
              response: { summary: '', error: errMsg, entryIndex: messageIndex },
            })),
          );
          return;
        }

        const reader = res.body.getReader();
        streamStarted = true;
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();

          for (const line of lines) {
            if (!line.trim()) continue;
            const json = parseLine(line);
            if (!json) continue;

            if (json.chat_entry) {
              const entry = json.chat_entry;
              setSessions((prev) =>
                applyToSession(prev, conversationId, (s) => {
                  const h = [...s.chatHistory];
                  if (entry.index < h.length) {
                    h[entry.index] = {
                      ...h[entry.index],
                      assistant: entry.assistant,
                      agents_left_out: entry.agents_left_out,
                      // Fallback chain: server → in-memory streaming → captured original
                      preliminary_answer:
                        entry.preliminary_answer ||
                        h[entry.index].preliminary_answer ||
                        originalConcise || undefined,
                    };
                  }
                  return { ...s, chatHistory: h };
                }),
              );
            }

            if (json.status) {
              notifyDegradedMode(json);
              const skip =
                ['initializing', 'loading_history'].includes(json.status) ||
                (json.status === 'result_generating' &&
                  json.message &&
                  (json.message.includes('direct response') || json.message.includes('concise response')));
              if (!skip) {
                setSessions((prev) =>
                  applyToSession(prev, conversationId, (s) => ({
                    ...s,
                    progressSteps: updateProgressSteps(s.progressSteps, json),
                  })),
                );
              }
            }

            if (json.summary_chunk) {
              accumulatedExpansive += json.summary_chunk;
              const current = accumulatedExpansive;

              if (!hasStartedStreaming) {
                // Preserve the original concise answer as preliminary_answer.
                // Use originalConcise (captured before streaming started) to avoid
                // reading stale or mid-update state via the session callback.
                if (originalConcise) {
                  setSessions((prev) =>
                    applyToSession(prev, conversationId, (s) => {
                      const h = [...s.chatHistory];
                      if (messageIndex < h.length && !h[messageIndex].preliminary_answer) {
                        h[messageIndex] = { ...h[messageIndex], preliminary_answer: originalConcise };
                      }
                      return { ...s, chatHistory: h };
                    }),
                  );
                }
                hasStartedStreaming = true;
              }

              setSessions((prev) =>
                applyToSession(prev, conversationId, (s) => {
                  const h = [...s.chatHistory];
                  if (messageIndex < h.length) {
                    h[messageIndex] = { ...h[messageIndex], assistant: current };
                  }
                  return { ...s, chatHistory: h };
                }),
              );
            }

            // The finally below clears progressSteps, and this is the
            // stream's last line, so an error has to go where it stays.
            if (json.error) {
              setSessions((prev) =>
                applyToSession(prev, conversationId, (s) => ({
                  ...s,
                  response: { summary: '', error: json.error, entryIndex: messageIndex },
                })),
              );
            }
          }
        }

      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error generating expansive answer:', error);
          setSessions((prev) =>
            applyToSession(prev, conversationId, (s) => ({
              ...s,
              response: droppedRunResponse(
                error, streamStarted, messageIndex, 'preliminary_answer',
              ),
            })),
          );
        }
      } finally {
        if (abortControllersRef.current[conversationId] === controller) {
          delete abortControllersRef.current[conversationId];
          setSessions((prev) =>
            applyToSession(prev, conversationId, (s) => {
              const h = [...s.chatHistory];
              // Guarantee preliminary_answer is set so hasPreliminary is true
              // after expansive completes, even when no summary_chunk events
              // arrived (agent went straight to tool calls) or React batching
              // prevented the streaming setter from committing in time.
              if (
                messageIndex < h.length &&
                !h[messageIndex].preliminary_answer &&
                originalConcise &&
                h[messageIndex].assistant !== originalConcise
              ) {
                h[messageIndex] = {
                  ...h[messageIndex],
                  preliminary_answer: originalConcise,
                };
              }
              return {
                ...s,
                loading: false,
                generatingExpansiveIndex: null,
                elapsed: performance.now() - startTime,
                progressSteps: [],
                chatHistory: h,
              };
            }),
          );
          if (typeof onExpansiveReady === 'function') onExpansiveReady(messageIndex);
          if (typeof onAfterAnswer === 'function') void onAfterAnswer(conversationId);
        }
      }
    },
    [API_URL, modelSettings],
  );

  // ─── /generate_evaluation ────────────────────────────────────────────────────

  const generateEvaluationReport = useCallback(
    async (conversationId, messageIndex, switchToEvalCallback, onAfterAnswer) => {
      if (sessionsRef.current[conversationId]?.loading) return;

      const controller = new AbortController();
      abortControllersRef.current[conversationId] = controller;
      const startTime = performance.now();

      setSessions((prev) =>
        applyToSession(prev, conversationId, (s) => ({
          ...s,
          loading: true,
          generatingEvaluationIndex: messageIndex,
          progressSteps: [],
          response: null,
        })),
      );

      let accumulatedEval = '';
      let hasStartedStreaming = false;
      let streamStarted = false;

      try {
        const res = await fetch(`${API_URL}/generate_evaluation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            conversation_id: conversationId,
            message_index: messageIndex,
            provider: modelSettings.provider,
            model: modelSettings.model,
            agent_models: modelSettings.agentModelAssignments,
          }),
          credentials: 'include',
          signal: controller.signal,
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({
            detail: `HTTP error! status: ${res.status}`,
          }));
          const errMsg = res.status === 409
            ? (errData.detail || 'A pipeline is already running for this conversation.')
            : (errData.detail || `HTTP error! status: ${res.status}`);
          console.error('Error generating evaluation report:', res.status, errMsg);
          setSessions((prev) =>
            applyToSession(prev, conversationId, (s) => ({
              ...s,
              loading: false,
              progressSteps: [],
              generatingEvaluationIndex: null,
              response: { summary: '', error: errMsg, entryIndex: messageIndex },
            })),
          );
          return;
        }

        const reader = res.body.getReader();
        streamStarted = true;
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();

          for (const line of lines) {
            if (!line.trim()) continue;
            const json = parseLine(line);
            if (!json) continue;

            if (json.chat_entry) {
              const entry = json.chat_entry;
              setSessions((prev) =>
                applyToSession(prev, conversationId, (s) => {
                  const h = [...s.chatHistory];
                  if (entry.index < h.length) {
                    h[entry.index] = {
                      ...h[entry.index],
                      evaluation_report: entry.evaluation_report,
                      evaluation_agents_left_out: entry.evaluation_agents_left_out,
                    };
                  }
                  return { ...s, chatHistory: h };
                }),
              );
            }

            if (json.status) {
              notifyDegradedMode(json);
              if (!['initializing', 'loading_history'].includes(json.status)) {
                setSessions((prev) =>
                  applyToSession(prev, conversationId, (s) => ({
                    ...s,
                    progressSteps: updateProgressSteps(s.progressSteps, json),
                  })),
                );
              }
            }

            if (json.summary_chunk) {
              accumulatedEval += json.summary_chunk;
              const current = accumulatedEval;

              if (!hasStartedStreaming) {
                if (switchToEvalCallback) switchToEvalCallback();
                hasStartedStreaming = true;
              }

              setSessions((prev) =>
                applyToSession(prev, conversationId, (s) => {
                  const h = [...s.chatHistory];
                  if (messageIndex < h.length) {
                    h[messageIndex] = { ...h[messageIndex], evaluation_report: current };
                  }
                  return { ...s, chatHistory: h };
                }),
              );
            }

            // The finally below clears progressSteps, and this is the
            // stream's last line, so an error has to go where it stays.
            if (json.error) {
              setSessions((prev) =>
                applyToSession(prev, conversationId, (s) => ({
                  ...s,
                  response: { summary: '', error: json.error, entryIndex: messageIndex },
                })),
              );
            }
          }
        }

      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error generating evaluation report:', error);
          setSessions((prev) =>
            applyToSession(prev, conversationId, (s) => ({
              ...s,
              response: droppedRunResponse(
                error, streamStarted, messageIndex, 'evaluation_report',
              ),
            })),
          );
        }
      } finally {
        if (abortControllersRef.current[conversationId] === controller) {
          delete abortControllersRef.current[conversationId];
          setSessions((prev) =>
            applyToSession(prev, conversationId, (s) => ({
              ...s,
              loading: false,
              generatingEvaluationIndex: null,
              elapsed: performance.now() - startTime,
              progressSteps: [],
            })),
          );
          if (typeof onAfterAnswer === 'function') void onAfterAnswer(conversationId);
        }
      }
    },
    [API_URL, modelSettings],
  );

  return {
    sessions,
    getSession,
    getLoadingIds,
    askQuestion,
    cancelRequest,
    loadSessionHistory,
    generateExpansiveAnswer,
    generateEvaluationReport,
  };
};
